/**
 * /me/documents — Session 3d PR 28.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { DocumentItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyDocumentsPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-documents", employeeId],
    queryFn: () => session3dApi.listMyDocuments(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<DocumentItem>[] = [
    {
      headerKey: "me.documents.col.type",
      label: "Document type",
      render: (r) => <span className="font-medium">{r.document_type}</span>,
    },
    {
      headerKey: "me.documents.col.number",
      label: "Number",
      render: (r) => <span className="font-mono text-xs">{r.document_number}</span>,
    },
    {
      headerKey: "me.documents.col.issue",
      label: "Issue date",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.issue_date ?? "—"}</span>,
    },
    {
      headerKey: "me.documents.col.expiry",
      label: "Expiry date",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.expiry_date ?? "—"}</span>,
    },
    {
      headerKey: "me.documents.col.days_until",
      label: "Days until expiry",
      render: (r) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
          r.status === "expired" ? "bg-rose-100 text-rose-800" :
          r.status === "expiring_soon" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
        }`}>
          {r.days_until_expiry ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <MeListPage<DocumentItem>
      title={t("me.documents.title", "My Documents")}
      subtitle={t("me.documents.subtitle", "Personal documents (iqama · passport · contract · certificates) with expiry tracking.")}
      domain="documents"
      khidmatPrefill={t("me.documents.ask_khidmat", "When does my iqama expire?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.document_number}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.documents.title", "My Documents"),
        window_label: t("me.documents.window", "all documents"),
      }}
      emptyHeadline={t("me.documents.empty_headline", "No documents on file.")}
      emptyBody={t("me.documents.empty_body", "Khidmat: HR uploads your documents during onboarding · they appear here.")}
    />
  );
}
